import { GRN_TYPE } from 'constants/grn/grnType';

// Pure helpers for the put-away instruction document. Kept free of any
// pdf/api imports so utils/pdfGenerators can use them without a cycle.

/** "PO-0055" / "GP-0012" for the GRN's source document. */
export const getInstructionGrnSourceCode = grn =>
  grn?.grn_type === GRN_TYPE.GATE_PASS
    ? grn?.gate_pass?.gate_pass_code
    : grn?.purchase_order?.po_code;

const compareShelves = (a, b) => {
  // higher priority = earlier on the warehouse route
  const priorityDiff = (b.priority ?? 0) - (a.priority ?? 0);
  if (priorityDiff !== 0) return priorityDiff;
  return String(a.code ?? '').localeCompare(String(b.code ?? ''));
};

const compareRows = (a, b) =>
  String(a.sku ?? '').localeCompare(String(b.sku ?? '')) ||
  String(a.inventoryType ?? '').localeCompare(String(b.inventoryType ?? '')) ||
  a.grnId - b.grnId;

/**
 * Flatten grns[].lines[].categories[] into shelf groups in walking order
 * (shelf.priority desc → shelf.code asc). Rows inside a shelf are sorted by
 * SKU → grade → GRN id. Items without a shelf are collected last.
 *
 * Returns { groups, totalQuantity, totalRows }.
 */
export const groupInstructionByShelf = instruction => {
  const groupsByKey = new Map();
  let totalQuantity = 0;
  let totalRows = 0;

  (instruction?.grns ?? []).forEach(grn => {
    const grnSourceCode = getInstructionGrnSourceCode(grn);

    (grn.lines ?? []).forEach(line => {
      (line.categories ?? []).forEach(category => {
        const shelf = category.shelf;
        const key = shelf?.id ?? 'no-shelf';

        if (!groupsByKey.has(key)) {
          groupsByKey.set(key, {
            shelf: shelf ?? null,
            rows: [],
            quantity: 0,
          });
        }

        const group = groupsByKey.get(key);
        group.rows.push({
          grnId: grn.id,
          grnSourceCode,
          sku: line.product?.sku,
          productName: line.product?.name,
          batchCode: category.product_batch?.batch_code,
          inventoryType: category.inventory_type,
          quantity: category.quantity,
        });
        group.quantity += category.quantity ?? 0;
        totalQuantity += category.quantity ?? 0;
        totalRows += 1;
      });
    });
  });

  const groups = [...groupsByKey.values()]
    .map(group => ({ ...group, rows: [...group.rows].sort(compareRows) }))
    .sort((a, b) => {
      if (!a.shelf && !b.shelf) return 0;
      if (!a.shelf) return 1;
      if (!b.shelf) return -1;
      return compareShelves(a.shelf, b.shelf);
    });

  return { groups, totalQuantity, totalRows };
};
